/**
 * THE CREDIT REQUEST (FR-U-CRD-002, FR-U-CRD-003, FR-X-CRD-002).
 *
 *   Product  ->  Amount, tenor and purpose  ->  Collateral  ->  Review  ->  Submit
 *
 * ## THE COLLATERAL STEP IS NOT OPTIONAL, AND IT IS NOT A FILE UPLOAD
 *
 * A borrowing request here is SECURED. The borrower selects the tokenized asset and the QUANTITY of
 * it to pledge, in basis points of the token, because that is how ownership is expressed everywhere
 * else on this platform. There is no free-text "collateral" field: the request points at a real
 * holding, and the pledge is a claim on real units that stops being available the moment it is made.
 *
 * ## THE LTV CEILING IS THE SERVER'S, NOT THIS SCREEN'S
 *
 * The maximum is recomputed here after every pledge, from the figures the server returns, so a
 * borrower is not left to discover at submit time that they asked for too much. But this screen is a
 * courtesy: the ceiling is enforced in the credit service AND by a database trigger, and a request
 * above it is refused there whatever this component believes.
 *
 * ## WHAT HAPPENS AFTER SUBMIT
 *
 * Nothing is borrowed. The request goes to platform review, collateral verification and the Trust,
 * and only then becomes visible to financiers. An offer is not a loan; acceptance is not a loan; the
 * loan exists when a financier gives its final approval.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  api,
  LipaApiError,
  type CollateralPosition,
  type CreditProduct,
  type EligibleCollateral,
} from '@/lib/api';
import { PageHeader } from '@/components/dash';
import { ErrorState, LoadingCard } from '@/components/states';
import { Reveal } from '@/components/motion';
import { formatWireAmount, sanitizeAmountTyping, toBaseUnits } from '@/lib/money';

const STEPS = ['Product', 'Terms', 'Collateral', 'Review'];

export default function CreditApply() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [products, setProducts] = useState<CreditProduct[] | null>(null);
  const [holdings, setHoldings] = useState<EligibleCollateral[]>([]);
  const [hasEligible, setHasEligible] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [step, setStep] = useState(0);
  const [productId, setProductId] = useState<string | null>(params.get('product'));
  const [amount, setAmount] = useState('');
  const [tenor, setTenor] = useState<number | null>(null);
  const [frequency, setFrequency] = useState<string | null>(null);
  const [purpose, setPurpose] = useState('');

  /** The draft request, created when the borrower reaches the collateral step. */
  const [requestId, setRequestId] = useState<string | null>(null);
  const [positions, setPositions] = useState<CollateralPosition[]>([]);
  const [maxFinancing, setMaxFinancing] = useState<string>('0');
  const [eligibleCollateral, setEligibleCollateral] = useState<string>('0');

  const [pledgeToken, setPledgeToken] = useState<string | null>(null);
  const [pledgeBps, setPledgeBps] = useState<number>(0);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const product = useMemo(
    () => products?.find((p) => p.id === productId) ?? null,
    [products, productId],
  );

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [p, c] = await Promise.all([
        api.creditMarket.products(),
        api.creditMarket.eligibleCollateral(),
      ]);
      setProducts(p.products);
      setHoldings(c.collateral);
      setHasEligible(c.has_any);
    } catch (e) {
      setLoadError(e instanceof LipaApiError ? e.detail.message : 'We could not load this page.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // A product arriving from the marketplace link skips step one, and seeds the terms the product
  // itself defines. Defaults come FROM the product, never from a constant here.
  useEffect(() => {
    if (!product) return;
    setTenor((t) => t ?? product.tenor_options_months[0] ?? Math.round(product.tenor_days / 30));
    setFrequency((f) => f ?? product.repayment_frequency);
    setStep((s) => (s === 0 ? 1 : s));
  }, [product]);

  const refreshRequest = useCallback(async (id: string) => {
    const detail = await api.creditMarket.request(id);
    setPositions(detail.collateral);
    setMaxFinancing(detail.max_financing_minor ?? '0');
    setEligibleCollateral(detail.eligible_collateral_minor ?? '0');
  }, []);

  if (loadError) return <ErrorState body={loadError} onRetry={() => void load()} />;
  if (!products) return <LoadingCard lines={5} />;

  const amountMinor = amount ? String(toBaseUnits(amount)) : '0';
  const withinLtv = BigInt(amountMinor) <= BigInt(maxFinancing || '0');
  const eligibleHoldings = holdings.filter(
    (h) => h.eligible && (!product || product.asset_classes.includes(h.asset_class)),
  );

  /** Create the draft, then move to collateral. The draft is where the pledges hang. */
  async function startCollateral() {
    if (!product || !tenor) return;
    setBusy(true);
    setError(null);
    try {
      const created =
        requestId ??
        (
          await api.creditMarket.createRequest({
            product_id: product.id,
            amount,
            tenor_months: tenor,
            purpose,
            ...(frequency ? { repayment_frequency: frequency } : {}),
          })
        ).id;
      setRequestId(created);
      await refreshRequest(created);
      setStep(2);
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not start this request.');
    } finally {
      setBusy(false);
    }
  }

  async function pledge() {
    if (!requestId || !pledgeToken || pledgeBps <= 0) return;
    setBusy(true);
    setError(null);
    try {
      await api.creditMarket.pledge(requestId, { token_id: pledgeToken, bps: pledgeBps });
      await refreshRequest(requestId);
      // Re-read availability: the units just pledged are no longer available anywhere.
      const c = await api.creditMarket.eligibleCollateral();
      setHoldings(c.collateral);
      setPledgeToken(null);
      setPledgeBps(0);
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not pledge that.');
    } finally {
      setBusy(false);
    }
  }

  async function unpledge(positionId: string) {
    if (!requestId) return;
    setBusy(true);
    try {
      await api.creditMarket.unpledge(requestId, positionId);
      await refreshRequest(requestId);
      const c = await api.creditMarket.eligibleCollateral();
      setHoldings(c.collateral);
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not release that pledge.');
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    if (!requestId) return;
    setBusy(true);
    setError(null);
    try {
      await api.creditMarket.submitRequest(requestId);
      navigate(`/credit/requests/${requestId}`);
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not submit this request.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <PageHeader title="Request financing" subtitle="Secured on a tokenized asset you own." />

      <ol className="flex items-center gap-1.5" aria-label="Progress">
        {STEPS.map((label, i) => (
          <li key={label} className="flex-1">
            <div
              className={`h-1 rounded-full ${i <= step ? 'bg-primary' : 'bg-surface-sunken'}`}
              aria-current={i === step ? 'step' : undefined}
            />
            <p
              className={`mt-1 text-nano ${i === step ? 'font-medium text-ink' : 'text-ink-subtle'}`}
            >
              {label}
            </p>
          </li>
        ))}
      </ol>

      {error && (
        <div
          role="alert"
          className="rounded-card border border-danger/40 bg-danger/5 p-3 text-sm text-ink"
        >
          {error}
        </div>
      )}

      {/* STEP 1: the product. */}
      {step === 0 && (
        <section className="space-y-2.5">
          {!hasEligible && <NoCollateralNotice holdings={holdings} />}
          {products.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setProductId(p.id)}
              className={`w-full rounded-card border p-3.5 text-left transition ${
                productId === p.id
                  ? 'border-primary bg-primary/5'
                  : 'border-border bg-surface hover:border-primary/40'
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium text-ink">{p.name}</span>
                <span className="tabular text-micro text-brand">
                  {(p.rate_bps / 100).toFixed(2)}% · max LTV {p.max_ltv_bps / 100}%
                </span>
              </div>
              {p.description && (
                <p className="mt-1 text-micro text-ink-muted">{p.description}</p>
              )}
            </button>
          ))}
        </section>
      )}

      {/* STEP 2: how much, for how long, and what for. */}
      {step === 1 && product && (
        <section className="space-y-4 rounded-card border border-border bg-surface p-4">
          <Field label="Amount requested" hint={`KES, ${product.name}`}>
            <div className="flex items-baseline gap-2">
              <span className="text-sm text-ink-muted">KES</span>
              <input
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(sanitizeAmountTyping(e.target.value))}
                placeholder="0.00"
                className="tabular w-full bg-transparent text-2xl font-semibold text-ink outline-none placeholder:text-ink-subtle"
              />
            </div>
            {product.min_amount_minor && product.max_amount_minor && (
              <p className="mt-1 text-nano text-ink-subtle">
                This product lends KES {formatWireAmount(product.min_amount_minor)} to{' '}
                {formatWireAmount(product.max_amount_minor)}.
              </p>
            )}
          </Field>

          <Field label="Repayment period">
            <div className="flex flex-wrap gap-2">
              {(product.tenor_options_months.length > 0
                ? product.tenor_options_months
                : [Math.round(product.tenor_days / 30)]
              ).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setTenor(m)}
                  className={`rounded-field px-3 py-1.5 text-micro font-medium transition ${
                    tenor === m
                      ? 'bg-primary text-white'
                      : 'bg-surface-sunken text-ink hover:bg-border'
                  }`}
                >
                  {m} months
                </button>
              ))}
            </div>
          </Field>

          <Field
            label="Repayment preference"
            hint="Set by the product; shown so you know what you are agreeing to."
          >
            <p className="text-sm text-ink">
              {frequency === 'bullet'
                ? 'One payment at maturity'
                : frequency === 'quarterly'
                  ? 'Quarterly'
                  : frequency === 'seasonal'
                    ? 'Seasonal'
                    : 'Monthly'}
            </p>
          </Field>

          <Field label="Purpose" hint="What the financing is for. Financiers see this.">
            <textarea
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="Refinance the second phase of the development."
              className="w-full rounded-field border border-border bg-surface-sunken p-2.5 text-sm text-ink outline-none focus:border-primary"
            />
          </Field>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setStep(0)}
              className="rounded-field border border-border px-3.5 py-2 text-sm font-medium text-ink"
            >
              Back
            </button>
            <button
              type="button"
              disabled={busy || !amount || !tenor || purpose.trim().length < 3}
              onClick={() => void startCollateral()}
              className="flex-1 rounded-field bg-primary px-3.5 py-2 text-sm font-medium text-white transition hover:bg-primary-light disabled:opacity-40"
            >
              {busy ? 'Working…' : 'Choose collateral'}
            </button>
          </div>
        </section>
      )}

      {/* STEP 3: the collateral. The step that makes this a secured product. */}
      {step === 2 && product && (
        <section className="space-y-4">
          {eligibleHoldings.length === 0 ? (
            <NoCollateralNotice holdings={holdings} />
          ) : (
            <>
              <div className="rounded-card border border-border bg-surface p-4">
                <p className="text-sm font-medium text-ink">Select collateral</p>
                <p className="mt-1 text-micro text-ink-muted">
                  Choose the asset and how much of your holding to pledge. Pledged units are locked
                  and cannot be sold or pledged again.
                </p>

                <ul className="mt-3 space-y-2.5">
                  {eligibleHoldings.map((h) => {
                    const chosen = pledgeToken === h.token_id;
                    return (
                      <li key={h.token_id}>
                        <button
                          type="button"
                          onClick={() => {
                            setPledgeToken(h.token_id);
                            setPledgeBps(h.available_bps);
                          }}
                          className={`w-full rounded-field border p-3 text-left transition ${
                            chosen
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-primary/40'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-ink">
                                {h.asset_name}
                              </p>
                              <p className="text-nano text-ink-subtle">
                                You hold {(h.owned_bps / 100).toFixed(2)}% ·{' '}
                                {(h.available_bps / 100).toFixed(2)}% unpledged
                              </p>
                            </div>
                            <span className="tabular shrink-0 text-micro font-medium text-ink">
                              KES {formatWireAmount(h.valuation_minor)}
                            </span>
                          </div>
                        </button>

                        {chosen && (
                          <div className="mt-2 rounded-field bg-surface-sunken p-3">
                            <label
                              htmlFor={`bps-${h.token_id}`}
                              className="flex items-center justify-between text-micro text-ink-muted"
                            >
                              <span>How much to pledge</span>
                              <span className="tabular font-medium text-ink">
                                {(pledgeBps / 100).toFixed(2)}%
                              </span>
                            </label>
                            <input
                              id={`bps-${h.token_id}`}
                              type="range"
                              min={1}
                              max={h.available_bps}
                              value={pledgeBps}
                              onChange={(e) => setPledgeBps(Number(e.target.value))}
                              className="mt-2 w-full accent-[color:var(--color-primary,#00482D)]"
                            />
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => void pledge()}
                              className="mt-2 w-full rounded-field bg-primary px-3 py-1.5 text-micro font-medium text-white disabled:opacity-40"
                            >
                              {busy ? 'Pledging…' : 'Use as collateral'}
                            </button>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>

              {positions.length > 0 && (
                <Reveal>
                  <div className="rounded-card border border-border bg-surface p-4">
                    <p className="text-sm font-medium text-ink">Pledged</p>
                    <ul className="mt-2 space-y-1.5">
                      {positions.map((p) => (
                        <li
                          key={p.id}
                          className="flex items-center justify-between gap-3 text-micro"
                        >
                          <span className="min-w-0 truncate text-ink">
                            {p.asset_name} · {(p.pledged_bps / 100).toFixed(2)}%
                          </span>
                          <button
                            type="button"
                            onClick={() => void unpledge(p.id)}
                            className="shrink-0 text-danger"
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>

                    {/* THE ARITHMETIC, SHOWN. Section 8 asks for exactly these four figures. */}
                    <dl className="mt-3 space-y-1.5 border-t border-border pt-3">
                      <Row
                        label="Eligible collateral value"
                        value={`KES ${formatWireAmount(eligibleCollateral)}`}
                      />
                      <Row label="Maximum loan to value" value={`${product.max_ltv_bps / 100}%`} />
                      <Row
                        label="Maximum eligible financing"
                        value={`KES ${formatWireAmount(maxFinancing)}`}
                        accent
                      />
                      <Row
                        label="Requested amount"
                        value={`KES ${formatWireAmount(amountMinor)}`}
                        danger={!withinLtv}
                      />
                    </dl>

                    {!withinLtv && (
                      <p className="mt-2 rounded-field bg-danger/5 p-2.5 text-micro text-ink">
                        You have asked for more than this collateral supports. Pledge more, or go
                        back and reduce the amount.
                      </p>
                    )}
                  </div>
                </Reveal>
              )}

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="rounded-field border border-border px-3.5 py-2 text-sm font-medium text-ink"
                >
                  Back
                </button>
                <button
                  type="button"
                  disabled={positions.length === 0 || !withinLtv}
                  onClick={() => setStep(3)}
                  className="flex-1 rounded-field bg-primary px-3.5 py-2 text-sm font-medium text-white transition hover:bg-primary-light disabled:opacity-40"
                >
                  Review
                </button>
              </div>
            </>
          )}
        </section>
      )}

      {/* STEP 4: review, then submit. */}
      {step === 3 && product && (
        <section className="space-y-4">
          <div className="rounded-card border border-border bg-surface p-4">
            <p className="text-sm font-medium text-ink">Credit request</p>
            <dl className="mt-3 space-y-2">
              <Row label="Product" value={product.name} />
              <Row label="Amount requested" value={`KES ${formatWireAmount(amountMinor)}`} />
              <Row label="Tenor" value={`${tenor} months`} />
              <Row
                label="Collateral"
                value={positions.map((p) => p.asset_name ?? p.token_id).join(', ')}
              />
              <Row
                label="Collateral value"
                value={`KES ${formatWireAmount(eligibleCollateral)}`}
              />
              <Row label="Maximum LTV" value={`${product.max_ltv_bps / 100}%`} />
              <Row label="Maximum financing" value={`KES ${formatWireAmount(maxFinancing)}`} />
              <Row label="Purpose" value={purpose} />
            </dl>
          </div>

          <p className="rounded-card bg-surface-sunken p-3 text-xs leading-relaxed text-ink-muted">
            Submitting sends this to platform review, then to the Trust, which verifies your
            ownership and places a hold on the pledged units. It then becomes visible to eligible
            financiers, who may offer terms. An offer is not a loan: you compare the offers, accept
            one, and the financier gives its final approval before anything is disbursed.
          </p>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setStep(2)}
              className="rounded-field border border-border px-3.5 py-2 text-sm font-medium text-ink"
            >
              Back
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void submit()}
              className="flex-1 rounded-field bg-primary px-3.5 py-2 text-sm font-medium text-white transition hover:bg-primary-light disabled:opacity-40"
            >
              {busy ? 'Submitting…' : 'Submit borrowing request'}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

/**
 * The dead end that is not one.
 *
 * Section 6: a borrower with nothing eligible to pledge cannot submit a secured request, and the
 * right answer is the route to tokenization rather than a disabled button with no explanation.
 */
function NoCollateralNotice({ holdings }: { holdings: EligibleCollateral[] }) {
  return (
    <div className="rounded-card border border-warning/40 bg-warning/5 p-4">
      <p className="text-sm font-medium text-ink">
        You don&rsquo;t currently have an eligible tokenized asset.
      </p>
      <p className="mt-1.5 text-micro leading-relaxed text-ink-muted">
        To borrow against an asset, you first need to tokenize and list the asset.
      </p>
      {holdings.length > 0 && (
        <ul className="mt-3 space-y-1.5 border-t border-warning/20 pt-3">
          {holdings.map((h) => (
            <li key={h.token_id} className="text-micro text-ink-muted">
              <span className="font-medium text-ink">{h.asset_name}</span>
              {h.reasons[0] && <> &mdash; {h.reasons[0]}</>}
            </li>
          ))}
        </ul>
      )}
      <Link
        to="/tokenize/new"
        className="mt-3 inline-block rounded-field bg-primary px-3.5 py-2 text-sm font-medium text-white transition hover:bg-primary-light"
      >
        Tokenize an Asset
      </Link>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-micro font-medium text-ink">{label}</p>
      {hint && <p className="text-nano text-ink-subtle">{hint}</p>}
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Row({
  label,
  value,
  accent,
  danger,
}: {
  label: string;
  value: string;
  accent?: boolean;
  danger?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="shrink-0 text-micro text-ink-subtle">{label}</dt>
      <dd
        className={`tabular min-w-0 truncate text-right text-micro font-medium ${
          danger ? 'text-danger' : accent ? 'text-brand' : 'text-ink'
        }`}
      >
        {value}
      </dd>
    </div>
  );
}
